/**
 * App Component
 *
 * Main application component with routing configuration.
 */

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';

import { AuthGuard, GuestGuard, PermissionGuard } from '@/components/guards';
import { MainLayout } from '@/components/layout/MainLayout';
import { useAuthStore } from '@/store';
import { useTheme } from '@/hooks';

// Pages
import { LoginPage } from '@/pages/auth/LoginPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { UnauthorizedPage } from '@/pages/errors/UnauthorizedPage';
import { NotFoundPage } from '@/pages/errors/NotFoundPage';
import {
  OrganizationsListPage,
  OrganizationDetailsPage,
  OrganizationUsersPage,
  PendingRegistrationsPage,
  UpgradePlanPage,
  RenewPlanPage,
} from '@/pages/organizations';
import { LoginAuditPage , GetLoginAuditHistoryPage} from '@/pages/audit';

import {
  UsersListPage,
  CreateUserPage,
  UserDetailsPage,
  EditUserPage,
} from '@/pages/users';
import { SubscriptionPlansPage } from '@/pages/subscriptions';
import { RolesPermissionsPage } from '@/pages/roles';
import { SettingsPage } from '@/pages/settings';
import { ProfilePage } from '@/pages/profile';
import { OtpVerificationPage } from '@/pages/support';

function App() {
  const initialize = useAuthStore((state) => state.initialize);
  const isInitialized = useAuthStore((state) => state.isInitialized);

  // Initialize theme on mount
  useTheme();

  // Initialize auth on mount
  useEffect(() => {
    void initialize();
  }, [initialize]);

  // Show loading while initializing
  if (!isInitialized) {
    return (
      <div className="flex h-screen items-center justify-center bg-background dark:bg-background-dark">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <BrowserRouter>
      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          className: 'dark:bg-card-dark dark:text-foreground-dark dark:border-border-dark',
        }}
      />

      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          }
        />

        {/* Protected routes with layout */}
        <Route
          element={
            <AuthGuard>
              <MainLayout />
            </AuthGuard>
          }
        >
          {/* Dashboard - accessible to all authenticated users */}
          <Route path="/dashboard" element={<DashboardPage />} />

          {/* Organizations */}
          <Route
            path="/organizations"
            element={
              <PermissionGuard perm="organization.organization.view">
                <OrganizationsListPage />
              </PermissionGuard>
            }
          />
          <Route
            path="/organizations/:id"
            element={
              <PermissionGuard perm="organization.organization.view">
                <OrganizationDetailsPage />
              </PermissionGuard>
            }
          />
          <Route
            path="/organizations/:id/users"
            element={
              <PermissionGuard perm="organization.organization.view">
                <OrganizationUsersPage />
              </PermissionGuard>
            }
          />
          <Route
            path="/organizations/:id/upgrade"
            element={
              <PermissionGuard perm="orgPlan.plan.manage">
                <UpgradePlanPage />
              </PermissionGuard>
            }
          />
          <Route
            path="/organizations/:id/renew"
            element={
              <PermissionGuard perm="orgPlan.plan.manage">
                <RenewPlanPage />
              </PermissionGuard>
            }
          />

          {/* Pending Registrations */}
          <Route
            path="/pending-registrations"
            element={
              <PermissionGuard perm="organization.organization.registration">
                <PendingRegistrationsPage />
              </PermissionGuard>
            }
          />

          {/* Users */}
          <Route
            path="/users"
            element={
              <PermissionGuard perm="orgUser.user.view">
                <UsersListPage />
              </PermissionGuard>
            }
          />
          <Route
            path="/users/create"
            element={
              <PermissionGuard perm="orgUser.user.manage">
                <CreateUserPage />
              </PermissionGuard>
            }
          />
          <Route
            path="/users/:id"
            element={
              <PermissionGuard perm="orgUser.user.view">
                <UserDetailsPage />
              </PermissionGuard>
            }
          />
          <Route
            path="/users/:id/edit"
            element={
              <PermissionGuard perm="orgUser.user.manage">
                <EditUserPage />
              </PermissionGuard>
            }
          />

          {/* Subscriptions */}
          <Route
            path="/subscriptions"
            element={
              <PermissionGuard perm="orgPlan.plan.view">
                <SubscriptionPlansPage />
              </PermissionGuard>
            }
          />

          {/* Audit */}
          <Route
            path="/audit"
            element={
              <PermissionGuard perm="audit.audit.view">
                <LoginAuditPage />
              </PermissionGuard>
            }
          />
          {/* Audit History */}
          <Route
            path="/audit/:orgId"
            element={
              <PermissionGuard perm="audit.audit.view">
                <GetLoginAuditHistoryPage />
              </PermissionGuard>
            }
          />
          {/* Support OTP */}
          <Route
            path="/support/:orgId/:userId/otp"
            element={
              <PermissionGuard perm="organization.support.manage">
                <OtpVerificationPage />
              </PermissionGuard>
            }
          />

          {/* Roles & Permissions */}
          <Route
            path="/roles"
            element={
              <PermissionGuard perm="userMgmt.role.view">
                <RolesPermissionsPage />
              </PermissionGuard>
            }
          />

          {/* Profile - all authenticated */}
          <Route path="/profile" element={<ProfilePage />} />

          {/* Settings - all authenticated (no dedicated permission key) */}
          <Route path="/settings" element={<SettingsPage />} />
        </Route>

        {/* Error pages */}
        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        {/* Redirects */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* 404 catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
